import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
  PenLine,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CandidateDoneCard } from "@/components/candidate/CandidateDoneCard";
import { CandidateFeedbackCard } from "@/components/candidate/CandidateFeedbackCard";
import { CandidateTimeUpCard } from "@/components/candidate/CandidateTimeUpCard";
import { ReportProblemTrigger } from "@/components/candidate/ReportProblemDialog";
import {
  bounceToGate,
  clearAttemptId,
  clearCandidateMeta,
  emitEvent,
  loadAttemptId,
  loadCandidateMeta,
  loadSubmittedAttemptId,
  loadTimeUpState,
  parseTimeLimitReached,
  saveSubmittedAttemptId,
  saveTimeUpState,
  submitAttempt,
  type CandidateMeta,
} from "@/lib/api";
import { clearAttemptAnswerDrafts } from "@/lib/answerDraft";
import { answerSaveFailures, flushAnswerSaves } from "@/lib/answerQueue";
import { flushReplayUploads } from "@/lib/codeReplayRecorder";
import { isAttemptGone, isSectionsRemaining } from "@/lib/candidateError";
import { disposeActiveStream } from "@/anticheat/camera";
import { exitFullscreen } from "@/anticheat/fullscreen";
import { useResultPoll } from "@/lib/useResultPoll";
import { cn, parseServerDate } from "@/lib/utils";

// "time_up" is a THIRD terminal state, not a flavour of "error": the candidate's
// clock ran out, which is an expected way for an attempt to end. It used to fall
// into "error" and render the red "We couldn't finalize your submission" card
// with a raw `API 410: {…}` body underneath (BUG-6).
// `sections_remaining` (OPS-28 #2): the safety-net /submit was refused because
// a section is still owed — a LIVE attempt that reached this page too early
// (a blip on the time-up section transition). Not a failure: the way back in.
type State = "submitting" | "ok" | "error" | "time_up" | "sections_remaining";

// OPS-28 #1: the B5 keeps go when the attempt is over — unless the queue is
// still holding an answer it could not save. That keep is the only copy of the
// candidate's answer left anywhere; a failure card with it gone is a dead end.
function clearKeepsUnlessUnsaved(attemptId: string): void {
  if (answerSaveFailures(attemptId).length > 0) return;
  clearAttemptAnswerDrafts(attemptId);
}

// Issue #7 / OPS-24: server-side hidden-test grading lands ~3-5s after
// /submit, or minutes later when the grading sweep has to pick the attempt up
// after a restart. The poll's schedule, bound and copy thresholds live in
// `@/lib/useResultPoll` — it survives a failed tick, which this page's own
// 15 s `refetchInterval` did not (chips spun forever until a reload).

function formatTime(iso: string): string {
  try {
    return parseServerDate(iso).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Done() {
  const { token = "demo" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("submitting");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(
    null,
  );
  // Per-company time-up copy, delivered in the terminal 410's detail (the only
  // channel that survives a refresh — see parseTimeLimitReached). null => the
  // card's default copy.
  const [timeLimitMessage, setTimeLimitMessage] = useState<string | null>(null);
  // CP-20 — whether this test asks for feedback, and whether this candidate
  // already gave it. Both come off the /submit response (read live server-
  // side), which re-runs on every mount, so a refresh rebuilds the card's
  // state with nothing kept in sessionStorage.
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [meta, setMeta] = useState<CandidateMeta | null>(null);
  const [submittedAttemptId, setSubmittedAttemptId] = useState<string | null>(
    null,
  );
  // Set once when submit succeeds: the clock the poll's backoff, copy
  // thresholds and 15-minute bound are measured from.
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    // Release the webcam the instant the candidate lands here — submit clicks
    // and time-up both route through this page, so this is the single point
    // that guarantees the camera light goes off as soon as the test ends.
    disposeActiveStream();
    // Release fullscreen too — the test is over, no reason to keep the
    // candidate locked in. MVP 2.0 #8.
    void exitFullscreen().catch(() => {});

    setMeta(loadCandidateMeta(token));

    // Two entry paths:
    //  1. First mount after manual submit / auto-submit — `attemptId` is the
    //     in-flight handle saved by /start.
    //  2. Candidate refreshed /done (e.g., to check whether a pending code
    //     score has materialised) — `attemptId` was cleared on the previous
    //     run, but `submittedAttemptId` still points at the same row.
    const inFlightAttemptId = loadAttemptId(token);
    const attemptId = inFlightAttemptId ?? loadSubmittedAttemptId(token);
    if (!attemptId) {
      setState("error");
      setErrorMessage("No active attempt was found for this link.");
      return;
    }

    void (async () => {
      if (inFlightAttemptId) {
        // First-mount only — don't duplicate the lifecycle event on refresh.
        // OPS-28 #30: through the bounded-retry emitter (one try used to be
        // all it got), fire-and-forget so the submit below never waits on it.
        emitEvent(token, attemptId, { type: "attempt_submitted" });
      }
      try {
        // Belt-and-suspenders: every intended route to Done already flushes the
        // answer-save queue before submitting, but a direct/stale /done navigation
        // might arrive with saves still queued. Drain them so this idempotent
        // safety-net submit never grades ahead of a pending write.
        await flushAnswerSaves().catch(() => {});
        // FEAT-31: the same for a code-replay chunk (bounded, never throws).
        await flushReplayUploads();
        // /submit is idempotent — returns the original submitted_at on refresh.
        const submitResult = await submitAttempt(token, attemptId);
        setSubmittedAt(submitResult.submitted_at);
        // Per-company completion message (MVP 4.0); null => default copy only.
        setCompletionMessage(submitResult.completion_message ?? null);
        setFeedbackEnabled(submitResult.feedback_enabled);
        setFeedbackGiven(submitResult.feedback_given);
        saveSubmittedAttemptId(token, attemptId);
        setSubmittedAttemptId(attemptId);
        setPollStartedAt(Date.now());

        clearAttemptId(token);
        clearCandidateMeta(token);
        // B5: the answers kept in the tab across a reload — submitted now
        // (kept while the queue still holds one it could not save, OPS-28 #1).
        clearKeepsUnlessUnsaved(attemptId);
        setState("ok");
      } catch (err) {
        // Keep the attempt id so the candidate can still "report a problem"
        // from either terminal state — a failed submit (or a timeout caused by
        // a technical issue) is exactly when they'd want to flag it. Doesn't
        // start the result poll (that's gated on state==="ok").
        setSubmittedAttemptId(attemptId);
        // The clock ran out: an expected terminal state, so it gets the calm
        // time-up card instead of the failure card. Deliberately NARROW — only
        // the structured terminal 410 qualifies, so a 500, a network error or
        // any other 410 still surfaces as a real error rather than being
        // silently dressed up as "you ran out of time". The attempt id is kept
        // here (unlike the success path), which is what lets a refresh re-run
        // this submit, hit the same 410, and re-show this card. B5's answer
        // keeps go, though: the attempt is over and nothing restores them.
        const timeUp = parseTimeLimitReached(err);
        if (timeUp) {
          saveTimeUpState(token, attemptId, timeUp.timeLimitMessage);
          clearKeepsUnlessUnsaved(attemptId);
          setTimeLimitMessage(timeUp.timeLimitMessage);
          setState("time_up");
          return;
        }
        // OPS-28 #2: a section is still owed — the attempt is LIVE and the
        // gate resumes it at the next section. The id stays (the gate reads
        // it), and so do the keeps.
        if (isSectionsRemaining(err)) {
          setState("sections_remaining");
          return;
        }
        // The submit failed for some OTHER reason — but if this attempt is
        // already known to have timed out, the time-up card is still the
        // truthful screen and there is nothing a failure card would let the
        // candidate do about it. The reachable case is the /submit rate limit
        // (5/min): past the 5th reload in a minute the 429 arrives instead of
        // the 410, and without this the calm card would flip to the red one.
        const remembered = loadTimeUpState(token, attemptId);
        if (remembered) {
          clearKeepsUnlessUnsaved(attemptId);
          setTimeLimitMessage(remembered.timeLimitMessage);
          setState("time_up");
          return;
        }
        // 10-07 S11: voided before it was submitted (an invitation replaced
        // this public-link attempt, or a retake) or erased — nothing to
        // finalize, and the raw "API 404" card is a dead end. Drop the id and
        // run the Gate again, exactly as the question screen does.
        if (inFlightAttemptId && isAttemptGone(err)) {
          clearCandidateMeta(token);
          bounceToGate(token, navigate);
          return;
        }
        setState("error");
        setErrorMessage(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [token, navigate]);

  const { query: resultQuery, phase: pollPhase } = useResultPoll({
    token,
    attemptId: submittedAttemptId,
    enabled: state === "ok",
    startedAt: pollStartedAt,
  });

  const result = resultQuery.data ?? null;
  const showScoreNumber =
    result?.show_score === true && result.total_score !== null;
  // EF-10: part of the test is scored by hand. Said plainly, instead of a
  // spinner that would run until the polling window closed and then a
  // "not ready yet" that never changed.
  const awaitingReview =
    result?.show_score === true &&
    result.total_score === null &&
    result.awaiting_review === true;
  // The three copies are the poll's phases: a spinner for the first seconds,
  // "isn't ready yet" while the poll keeps going (it does, through failed
  // ticks and a server restart — OPS-24), and after the 15-minute bound a
  // plain ask to reload. Before any response has landed, the spinner shows
  // whatever the clock says — a result that hides the score settles the poll
  // and clears all three.
  const stillPolling = pollPhase !== "idle" && pollPhase !== "gave_up";
  const showCalculating =
    state === "ok" && !awaitingReview && pollPhase === "calculating";
  const showScorePending =
    state === "ok" && !awaitingReview && pollPhase === "waiting";
  const showPollGaveUp =
    state === "ok" && !awaitingReview && pollPhase === "gave_up";

  // Candidate-only score region — passed into the shared Done card as a slot so
  // the card itself stays presentational (and reusable by the recruiter preview).
  // ⚠ EP-21: the result email's breakdown repeats this number — its headline
  // is `backend/scoring.py::done_screen_percent`, which reproduces this exact
  // `Math.round`. Change the rounding here, change it there.
  const scoreSlot = (
    <>
      {showScoreNumber && (
        <div className="font-mono text-2xl text-foreground">
          Your score: {Math.round((result!.total_score as number) * 100)}%
        </div>
      )}
      {awaitingReview && (
        <div className="flex items-start justify-center gap-2 text-sm text-muted-foreground">
          <PenLine className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Part of this test is scored by hand. A recruiter will review your
            written answer{result?.per_question &&
            result.per_question.filter((q) => q.pending_review).length > 1
              ? "s"
              : ""}
            , so your score isn&apos;t final yet.
          </span>
        </div>
      )}
      {showCalculating && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Calculating your score…
        </div>
      )}
      {showScorePending && (
        <div className="text-sm text-muted-foreground">
          Your score isn&apos;t ready yet. You&apos;ll see it here once it&apos;s
          been calculated.
        </div>
      )}
      {showPollGaveUp && (
        // Placeholder copy — the wording is Emilia's (owed.md). Reached only
        // after 15 minutes without a score; the page has stopped asking.
        <div className="text-sm text-muted-foreground">
          Your score is taking longer than usual. Reload this page to check
          again.
        </div>
      )}
      {result?.show_score === true &&
        result.per_question &&
        result.per_question.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
            {result.per_question.map((q) => {
              // EF-10: "review" is a static mark, never a spinner — nothing is
              // running, a person will read it.
              const status =
                q.pending_review === true
                  ? "review"
                  : q.score === null
                    ? "pending"
                    : q.score > 0
                      ? "correct"
                      : "wrong";
              return (
                <span
                  key={q.position}
                  title={
                    status === "review" ? "Scored by hand by a recruiter" : undefined
                  }

                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
                    status === "correct" &&
                      "border-success/30 bg-success/10 text-success",
                    status === "wrong" &&
                      "border-destructive/30 bg-destructive/10 text-destructive",
                    (status === "pending" || status === "review") &&
                      "border-border bg-muted/40 text-muted-foreground",
                  )}
                >
                  Q{q.position}
                  {/* The icon is the only status mark, so a screen reader
                      heard "Q1 Q2 Q3" (pre-ship 2026-10-18 QA-5). Placeholder
                      copy — the wording is Emilia's. */}
                  <span className="sr-only">
                    {status === "correct"
                      ? ": correct"
                      : status === "wrong"
                        ? ": incorrect"
                        : status === "review"
                          ? ": scored by hand by a recruiter"
                          : ": not scored yet"}
                  </span>
                  {status === "correct" && <CheckCircle2 className="h-3 w-3" />}
                  {status === "wrong" && <XCircle className="h-3 w-3" />}
                  {/* OPS-24: a spinner only while the page is still asking;
                      once the poll gave up it is a static mark, like review. */}
                  {status === "pending" && stillPolling && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  {status === "pending" && !stillPolling && (
                    <Clock className="h-3 w-3" aria-hidden="true" />
                  )}
                  {status === "review" && (
                    <PenLine className="h-3 w-3" aria-hidden="true" />
                  )}
                </span>
              );
            })}
          </div>
        )}
    </>
  );

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-background p-6">
      {state === "ok" ? (
        <CandidateDoneCard
          candidateName={meta?.candidate_name}
          testName={meta?.test_name}
          submittedAtLabel={submittedAt ? formatTime(submittedAt) : null}
          completionMessage={completionMessage}
          scoreSlot={scoreSlot}
        />
      ) : state === "time_up" ? (
        // No scoreSlot: this card is reached only when the lapse could NOT be
        // submitted (a non-last section past its deadline). A lapsed LAST
        // clock submits and grades since 2026-09-18, so it takes the `ok`
        // branch above like any other submit.
        <CandidateTimeUpCard
          candidateName={meta?.candidate_name}
          testName={meta?.test_name}
          timeLimitMessage={timeLimitMessage}
        />
      ) : state === "sections_remaining" ? (
        // OPS-28 #2 — ⚠ placeholder copy (wording is Emilia's). The gate
        // resumes a live attempt at its next section; before this card the
        // candidate saw the red "couldn't finalize" card with the raw 409
        // body, and nothing said the test could go on.
        <Card className="w-full max-w-lg text-center">
          <CardHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
              <ArrowRight className="h-6 w-6 text-info" aria-hidden="true" />
            </div>
            <CardTitle>Your test isn&rsquo;t finished yet</CardTitle>
            <CardDescription className="space-y-1">
              There is still a section to take. Your answers so far are saved
              &mdash; resume the test to continue where you left off.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="cursor-pointer">
              <Link to={`/t/${token}`} replace>
                Resume the test
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-lg text-center">
          <CardHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
              {state === "submitting" ? (
                <Loader2 className="h-6 w-6 animate-spin text-info" />
              ) : (
                <XCircle className="h-6 w-6 text-destructive" />
              )}
            </div>
            <CardTitle>
              {state === "submitting"
                ? "Submitting your responses…"
                : "We couldn't finalize your submission"}
            </CardTitle>
            <CardDescription className="space-y-1">
              {state === "submitting"
                ? "Hold on for a moment."
                : (errorMessage ?? "Please contact the recruiter.")}
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {/* CP-20 — the optional feedback box. Only after a SUCCESSFUL submit
          (never on the time-up or failure cards: the backend refuses an
          unsubmitted attempt anyway) and only when the recruiter turned it on
          for this test. Sits under the thank-you card, above the report link. */}
      {state === "ok" && feedbackEnabled && submittedAttemptId && (
        <CandidateFeedbackCard
          token={token}
          attemptId={submittedAttemptId}
          initiallyGiven={feedbackGiven}
        />
      )}
      {/* Subtle post-submit entry. Renders nothing until an attempt id exists
          (i.e. once state is "ok" or a failed-submit "error"), so it's hidden
          during "submitting". This is the ONLY report entry per-question-mode
          candidates get — they never see a Review screen. */}
      <ReportProblemTrigger token={token} attemptId={submittedAttemptId} />
    </div>
  );
}
